// services/frontend/src/utils/dateFormat.ts
// Shared date formatting utilities

import { formatDistanceToNow, format } from 'date-fns';

/**
 * Format a date as relative time (e.g., "2 hours ago", "yesterday", "3 days ago")
 * Falls back to absolute date for dates older than 7 days
 *
 * @param date - ISO string or Date object
 * @returns Formatted date string
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24));

  // For dates older than 7 days, show absolute date with time
  if (diffInDays > 7) {
    return format(dateObj, 'MMM d, yyyy'); // e.g., "Nov 16, 2025"
  }

  // For recent dates, show relative time
  return formatDistanceToNow(dateObj, { addSuffix: true }); // e.g., "2 hours ago"
}

/**
 * Format a date as full date and time
 *
 * @param date - ISO string or Date object
 * @returns Formatted date string (e.g., "Nov 16, 2025 at 2:51 PM")
 */
export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, "MMM d, yyyy 'at' h:mm a");
}

/**
 * Format a date as just the date (no time)
 *
 * @param date - ISO string or Date object
 * @returns Formatted date string (e.g., "Nov 16, 2025")
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, 'MMM d, yyyy');
}
